import React, { useState } from "react";

import styled from "styled-components";

import { colorTheme, gridSpacing } from "@utils";

import { AGGREGATION_LABELS } from "./aggregations";
import { defaultConfig } from "./engine";
import { SHOW_AS_LABELS, showAsSupported } from "./showAs";
import type {
  PivotAggregation,
  PivotConfig,
  PivotDateGrouping,
  PivotDimension,
  PivotShowAs,
  PivotTopN,
  PivotValue,
} from "./types";
import type { PivotController } from "./usePivotController";
import { defaultAggregation, fieldLabel } from "./valueUtils";
import DropDown from "../../DropDown/DropDown";
import Select from "../../Select/Select";
import Text from "../../Text/Text";
import ToolTip from "../../ToolTip/ToolTip";
import { TriStateCheckbox } from "../components/SelectionCells";
import TableV2IconButton from "../components/TableV2IconButton";

type Zone = "rows" | "columns" | "values";

interface DragPayload {
  fieldId: string;
  from: Zone | "fields";
  index: number;
}

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid ${colorTheme("neutralL4")};
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  background: ${colorTheme("neutralL6")};
  font-size: 14px;

  & + * [role="rowgroup"] > [role="row"]:first-child {
    border-radius: 0;
  }
`;

const Zones = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(0, 2fr);
`;

const Zone = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${gridSpacing[1]}px;
  min-width: 0;
  padding: ${gridSpacing[2]}px ${gridSpacing[3]}px;

  & + & {
    box-shadow: inset 1px 0 0 ${colorTheme("neutralL4")};
  }
`;

const ZoneTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${gridSpacing[1]}px;
  min-height: 22px;
  font-size: 12px;
  font-weight: bold;
  color: ${colorTheme("neutralL1")};
  text-transform: uppercase;
`;

const DropZone = styled.div<{ $active?: boolean; $dragging?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${gridSpacing[1]}px;
  min-height: 34px;
  padding: ${gridSpacing[1]}px;
  border: 1px dashed
    ${({ $active, $dragging }) =>
      $active
        ? colorTheme("primary")
        : $dragging
          ? colorTheme("neutralL3")
          : "transparent"};
  border-radius: 5px;
  background: ${({ $active }) =>
    $active ? colorTheme("primaryL4") : "transparent"};
`;

const Hint = styled.span`
  padding: 0 ${gridSpacing[1]}px;
  font-size: 12px;
  color: ${colorTheme("neutralL2")};
`;

const Chip = styled.div<{
  $dragging?: boolean;
  $muted?: boolean;
  $phrase?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  gap: ${({ $phrase }) => gridSpacing[$phrase ? 2 : 1]}px;
  box-sizing: border-box;
  max-width: 100%;
  height: ${({ $phrase }) => ($phrase ? 40 : 32)}px;
  padding: 0 ${gridSpacing[1]}px 0 ${gridSpacing[2]}px;
  border: 1px solid ${colorTheme("neutralL4")};
  border-radius: 4px;
  background: ${colorTheme("white")};
  color: ${({ $muted }) => ($muted ? colorTheme("neutralL2") : "inherit")};
  cursor: grab;
  opacity: ${({ $dragging }) => ($dragging ? 0.5 : 1)};

  &:hover {
    border-color: ${colorTheme("neutralL3")};
  }
`;

const Grip = styled.i`
  font-size: 11px;
  color: ${colorTheme("neutralL3")};
`;

const Connector = styled.span`
  font-size: 13px;
  color: ${colorTheme("neutralL2")};
`;

const ChipLabel = styled.span<{ $strong?: boolean }>`
  font-weight: ${({ $strong }) => ($strong ? 600 : "normal")};
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Band = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${gridSpacing[2]}px ${gridSpacing[4]}px;
  padding: ${gridSpacing[2]}px ${gridSpacing[3]}px;
  border-bottom: 1px solid ${colorTheme("neutralL4")};
`;

const FieldTray = styled.div`
  display: flex;
  flex: 1 1 0;
  flex-wrap: wrap;
  align-items: center;
  gap: ${gridSpacing[1]}px;
  min-width: 0;
`;

const Group = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${gridSpacing[2]}px;
`;

const GroupLabel = styled.span`
  font-size: 12px;
  font-weight: bold;
  color: ${colorTheme("neutralL1")};
  text-transform: uppercase;
`;

const OptionLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${gridSpacing[2]}px;
`;

const Spacer = styled.span`
  flex: 1;
`;

const DATE_GROUPINGS: PivotDateGrouping[] = [
  "year",
  "quarter",
  "month",
  "week",
  "day",
];

const compactSelect = {
  hideSearch: true,
  controlStyle: { minHeight: 32, height: 32 },
  valueContainerStyle: { padding: `0 ${gridSpacing[2]}px` },
  indicatorsContainerStyle: { height: 30 },
};

const capitalize = (text: string) => text[0].toUpperCase() + text.slice(1);

const newValueId = (config: PivotConfig, fieldId: string) => {
  let i = 1;
  while (config.values.some((value) => value.id === `${fieldId}_${i}`)) i += 1;
  return `${fieldId}_${i}`;
};

const PivotBuilder = <TData,>({
  pivot,
  onClose,
}: {
  pivot: PivotController<TData>;
  onClose?: () => void;
}) => {
  const { fields, setConfig } = pivot;
  const config = defaultConfig(pivot.config);
  const [drag, setDrag] = useState<DragPayload | null>(null);
  const [overZone, setOverZone] = useState<Zone | null>(null);

  const usedAsDimension = new Set(
    [...config.rows, ...config.columns].map((dim) => dim.field),
  );
  const field = (id: string) => fields.find((f) => f.id === id);

  const toDimension = (fieldId: string): PivotDimension =>
    field(fieldId)?.type === "date"
      ? { field: fieldId, dateGrouping: "month" }
      : { field: fieldId };

  const toValue = (current: PivotConfig, fieldId: string): PivotValue => {
    return {
      id: newValueId(current, fieldId),
      field: fieldId,
      aggregation: defaultAggregation(field(fieldId)),
    };
  };

  // Drops Top N rules and show-as choices the new layout no longer supports
  const tidy = (next: PivotConfig): PivotConfig => {
    return {
      ...next,
      values: next.values.map((value) =>
        value.showAs && !showAsSupported(value.showAs, next)
          ? { ...value, showAs: "value" }
          : value,
      ),
      topN: next.topN?.filter(
        (rule) =>
          next.rows.some((dim) => dim.field === rule.field) &&
          next.values.some((value) => value.id === rule.valueId),
      ),
    };
  };

  const move = (payload: DragPayload, to: Zone, toIndex?: number) => {
    setConfig((current) => {
      const next: PivotConfig = {
        ...current,
        rows: [...current.rows],
        columns: [...current.columns],
        values: [...current.values],
      };
      let dimension: PivotDimension | undefined;
      let value: PivotValue | undefined;
      if (payload.from === "rows" || payload.from === "columns") {
        [dimension] = next[payload.from].splice(payload.index, 1);
      } else if (payload.from === "values") {
        [value] = next.values.splice(payload.index, 1);
      }
      if (to === "values") {
        const item = value ?? toValue(next, payload.fieldId);
        next.values.splice(toIndex ?? next.values.length, 0, item);
      } else {
        next.rows = next.rows.filter((dim) => dim.field !== payload.fieldId);
        next.columns = next.columns.filter(
          (dim) => dim.field !== payload.fieldId,
        );
        const item = dimension ?? toDimension(payload.fieldId);
        const target = next[to];
        target.splice(
          Math.min(toIndex ?? target.length, target.length),
          0,
          item,
        );
      }
      return tidy(next);
    });
  };

  const remove = (zone: Zone, index: number) =>
    setConfig((current) =>
      tidy({
        ...current,
        [zone]: current[zone].filter((_, i) => i !== index),
      }),
    );

  const updateDimension = (
    zone: "rows" | "columns",
    index: number,
    patch: Partial<PivotDimension>,
  ) =>
    setConfig((current) =>
      tidy({
        ...current,
        [zone]: current[zone].map((dim, i) =>
          i === index ? { ...dim, ...patch } : dim,
        ),
      }),
    );

  const updateValue = (index: number, patch: Partial<PivotValue>) =>
    setConfig((current) => {
      return {
        ...current,
        values: current.values.map((value, i) =>
          i === index ? { ...value, ...patch } : value,
        ),
      };
    });

  const dragProps = (payload: DragPayload) => {
    return {
      draggable: true,
      onDragStart: (event: React.DragEvent) => {
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData("text/plain", payload.fieldId);
        setDrag(payload);
      },
      onDragEnd: () => {
        setDrag(null);
        setOverZone(null);
      },
    };
  };

  const zoneProps = (zone: Zone) => {
    return {
      $active: overZone === zone,
      $dragging: !!drag,
      onDragOver: (event: React.DragEvent) => {
        if (!drag) return;
        event.preventDefault();
        if (overZone !== zone) setOverZone(zone);
      },
      onDragLeave: () => setOverZone(null),
      onDrop: (event: React.DragEvent) => {
        event.preventDefault();
        if (drag) move(drag, zone);
        setDrag(null);
        setOverZone(null);
      },
    };
  };

  const chipDropProps = (zone: Zone, index: number) => {
    return {
      onDrop: (event: React.DragEvent) => {
        event.preventDefault();
        event.stopPropagation();
        if (drag) {
          const sameZoneBefore = drag.from === zone && drag.index < index;
          move(drag, zone, sameZoneBefore ? index - 1 : index);
        }
        setDrag(null);
        setOverZone(null);
      },
    };
  };

  const addMenu = (fieldId: string) => (
    <DropDown direction="down">
      <DropDown.Toggle>
        <TableV2IconButton
          tooltip={false}
          icon="fa-solid fa-plus"
          label={`Add ${fieldLabel(fields, fieldId)}`}
          onClick={() => undefined}
        />
      </DropDown.Toggle>
      <DropDown.Menu>
        {(["rows", "columns", "values"] as Zone[]).map((zone) => (
          <DropDown.Item
            key={zone}
            value={zone}
            onClick={() => move({ fieldId, from: "fields", index: -1 }, zone)}
          >
            Add to {zone}
          </DropDown.Item>
        ))}
      </DropDown.Menu>
    </DropDown>
  );

  const dimensionChip = (
    zone: "rows" | "columns",
    dim: PivotDimension,
    index: number,
  ) => {
    const label = fieldLabel(fields, dim.field);
    return (
      <Chip
        key={`${zone}-${dim.field}`}
        data-testid={`pivot-${zone}-${dim.field}`}
        $phrase={field(dim.field)?.type === "date"}
        $dragging={drag?.from === zone && drag.index === index}
        {...dragProps({ fieldId: dim.field, from: zone, index })}
        {...chipDropProps(zone, index)}
      >
        <Grip className="fa-solid fa-grip-vertical" />
        {field(dim.field)?.type === "date" ? (
          <>
            <ChipLabel $strong style={{ flexShrink: 0 }}>
              {label}
            </ChipLabel>
            <Connector>by</Connector>
            <Select
              testId={`pivot-grouping-${dim.field}`}
              {...compactSelect}
              style={{ width: 110, paddingBottom: 0 }}
              value={dim.dateGrouping ?? "month"}
              options={DATE_GROUPINGS.map((grouping) => {
                return { value: grouping, label: capitalize(grouping) };
              })}
              onChange={(next) =>
                updateDimension(zone, index, {
                  dateGrouping: next as PivotDateGrouping,
                })
              }
            />
          </>
        ) : (
          <ChipLabel>{label}</ChipLabel>
        )}
        <TableV2IconButton
          tooltip={false}
          icon="fa-solid fa-xmark"
          label={`Remove ${label}`}
          onClick={() => remove(zone, index)}
        />
      </Chip>
    );
  };

  const valueChip = (value: PivotValue, index: number) => {
    const label = fieldLabel(fields, value.field);
    return (
      <Chip
        $phrase
        key={value.id}
        data-testid={`pivot-values-${value.id}`}
        $dragging={drag?.from === "values" && drag.index === index}
        {...dragProps({ fieldId: value.field, from: "values", index })}
        {...chipDropProps("values", index)}
      >
        <Grip className="fa-solid fa-grip-vertical" />
        <Select
          testId={`pivot-aggregation-${value.id}`}
          {...compactSelect}
          style={{ width: 150, paddingBottom: 0 }}
          value={value.aggregation}
          options={(Object.keys(AGGREGATION_LABELS) as PivotAggregation[]).map(
            (aggregation) => {
              return {
                value: aggregation,
                label: AGGREGATION_LABELS[aggregation],
              };
            },
          )}
          onChange={(next) =>
            updateValue(index, { aggregation: next as PivotAggregation })
          }
        />
        <Connector>of</Connector>
        <ChipLabel style={{ flexShrink: 0, marginBottom: gridSpacing[1] }}>
          {label}
        </ChipLabel>
        <Connector>as</Connector>
        <Select
          testId={`pivot-show-as-${value.id}`}
          {...compactSelect}
          style={{ width: 230, paddingBottom: 0 }}
          value={value.showAs ?? "value"}
          options={(Object.keys(SHOW_AS_LABELS) as PivotShowAs[])
            .filter((showAs) => showAsSupported(showAs, config))
            .map((showAs) => {
              return { value: showAs, label: SHOW_AS_LABELS[showAs] };
            })}
          onChange={(next) =>
            updateValue(index, { showAs: next as PivotShowAs })
          }
        />
        <TableV2IconButton
          tooltip={false}
          icon="fa-solid fa-xmark"
          label={`Remove ${label}`}
          onClick={() => remove("values", index)}
        />
      </Chip>
    );
  };

  const topN: PivotTopN | undefined = config.topN?.[0];
  const setTopN = (patch: Partial<PivotTopN> | null) =>
    setConfig((current) => {
      if (patch === null) return { ...current, topN: undefined };
      const base: PivotTopN = current.topN?.[0] ?? {
        field: current.rows[0]?.field ?? "",
        n: 10,
        valueId: current.values[0]?.id ?? "",
        showOther: true,
      };
      return { ...current, topN: [{ ...base, ...patch }] };
    });

  const option = (
    key:
      | "rowSubtotals"
      | "columnSubtotals"
      | "rowGrandTotal"
      | "columnGrandTotal",
    text: string,
    short: string,
  ) => (
    <OptionLabel>
      <TriStateCheckbox
        checked={config[key]}
        label={text}
        onClick={() =>
          setConfig((current) => {
            return {
              ...current,
              [key]: !defaultConfig(current)[key],
            };
          })
        }
      />
      {short}
    </OptionLabel>
  );

  const zone = (name: Zone, title: string, items: React.ReactNode[]) => (
    <Zone>
      <ZoneTitle>{title}</ZoneTitle>
      <DropZone data-testid={`pivot-zone-${name}`} {...zoneProps(name)}>
        {items.length ? items : <Hint>Drag fields here</Hint>}
      </DropZone>
    </Zone>
  );

  return (
    <Panel data-testid="pivot-setup">
      <Band>
        <FieldTray>
          <GroupLabel>Fields</GroupLabel>
          {fields.map((f) => (
            <Chip
              key={f.id}
              data-testid={`pivot-field-${f.id}`}
              $muted={usedAsDimension.has(f.id)}
              {...dragProps({ fieldId: f.id, from: "fields", index: -1 })}
            >
              {usedAsDimension.has(f.id) ? (
                <ToolTip text="Already in rows or columns" direction="top">
                  <ChipLabel>{f.label}</ChipLabel>
                </ToolTip>
              ) : (
                <ChipLabel>{f.label}</ChipLabel>
              )}
              {addMenu(f.id)}
            </Chip>
          ))}
        </FieldTray>
        {onClose && (
          <TableV2IconButton
            icon="fa-solid fa-xmark"
            label="Close pivot setup"
            onClick={onClose}
          />
        )}
      </Band>
      <Band>
        <Group>
          <GroupLabel>Subtotals</GroupLabel>
          {option("rowSubtotals", "Row subtotals", "Rows")}
          {option("columnSubtotals", "Column subtotals", "Columns")}
        </Group>
        <Group>
          <GroupLabel>Grand totals</GroupLabel>
          {option("rowGrandTotal", "Grand total row", "Row")}
          {option("columnGrandTotal", "Grand total column", "Column")}
        </Group>
        <Group>
          <GroupLabel>Show</GroupLabel>
          <Select
            testId="pivot-top-n-mode"
            {...compactSelect}
            style={{ width: 110, paddingBottom: 0 }}
            value={topN ? (topN.bottom ? "bottom" : "top") : "all"}
            disabled={!config.rows.length || !config.values.length}
            options={[
              { value: "all", label: "All rows" },
              { value: "top", label: "Top" },
              { value: "bottom", label: "Bottom" },
            ]}
            onChange={(next) =>
              next === "all"
                ? setTopN(null)
                : setTopN({ bottom: next === "bottom" })
            }
          />
          {topN && (
            <>
              <Text
                type="number"
                min={1}
                testId="pivot-top-n-count"
                value={String(topN.n)}
                onChange={(event) =>
                  setTopN({ n: Math.max(1, Number(event.target.value) || 1) })
                }
                style={{ marginBottom: 0 }}
                labelStyle={{ display: "none" }}
                inputStyle={{ width: 64, height: 32, boxSizing: "border-box" }}
              />
              <Select
                testId="pivot-top-n-field"
                {...compactSelect}
                style={{ width: 140, paddingBottom: 0 }}
                value={topN.field}
                options={config.rows.map((dim) => {
                  return {
                    value: dim.field,
                    label: fieldLabel(fields, dim.field),
                  };
                })}
                onChange={(next) => setTopN({ field: next })}
              />
              by
              <Select
                testId="pivot-top-n-value"
                {...compactSelect}
                style={{ width: 140, paddingBottom: 0 }}
                value={topN.valueId}
                options={config.values.map((value) => {
                  return {
                    value: value.id,
                    label: fieldLabel(fields, value.field),
                  };
                })}
                onChange={(next) => setTopN({ valueId: next })}
              />
              <OptionLabel>
                <TriStateCheckbox
                  checked={topN.showOther !== false}
                  label="Group the rest as Other"
                  onClick={() =>
                    setTopN({ showOther: topN.showOther === false })
                  }
                />
                Other
              </OptionLabel>
            </>
          )}
        </Group>
        <Spacer />
        <TableV2IconButton
          icon="fa-solid fa-arrow-right-arrow-left"
          label="Swap rows and columns"
          onClick={() =>
            setConfig((current) => {
              return {
                ...current,
                rows: current.columns,
                columns: current.rows,
                topN: undefined,
              };
            })
          }
        />
      </Band>
      <Zones>
        {zone(
          "rows",
          "Rows",
          config.rows.map((dim, i) => dimensionChip("rows", dim, i)),
        )}
        {zone(
          "columns",
          "Columns",
          config.columns.map((dim, i) => dimensionChip("columns", dim, i)),
        )}
        {zone("values", "Values", config.values.map(valueChip))}
      </Zones>
    </Panel>
  );
};

export default PivotBuilder;
